import { StepEngine } from '../engine/stepEngine';
import { resolveScenarioByParam } from '../scenarios/index';
import { PlayerRuntime } from './playerRuntime';
import { ApiPersistenceAdapter, MockPersistenceAdapter } from './persistence';
import { PlayerApiClient } from '../api/client';
import { ensureDeviceKey } from '../utils/deviceKey';
import { migrateLegacyProgress } from '../utils/saveProgress';
import { ScenarioSchema, type ScenarioContent } from 'shared';

export interface BootstrapResult {
  runtime: PlayerRuntime;
  scenarioId: string;
  scenario: ScenarioContent;
}

/**
 * シナリオ content を ScenarioSchema で検証する。
 *
 * - 成功時: 何もしない。
 * - 失敗時（source 別に strict / warn を切り替える）:
 *   - DEV と mock/preview 経路は strict（throw → usePlayerRuntime の catch →
 *     App.tsx の ErrorUi05）。mock/preview が読むデータ（バンドル内モック JSON /
 *     Studio プレビュー）は ScenarioSchema 適合済みのため、壊れたら止めて良い。
 *   - live 経路の本番は当面 warn（throw しない）。T3b 導入前に publish された既存
 *     Supabase published データが ScenarioSchema に適合しないリスクがあり、本番体験を
 *     壊さないことを最優先する。既存 published 全件の ScenarioSchema 適合を確認後に
 *     live も strict 化する。
 */
function validateScenarioContent(scenario: unknown, source: string): void {
  const parsed = ScenarioSchema.safeParse(scenario);
  if (parsed.success) {
    return;
  }
  const summary = parsed.error.issues
    .slice(0, 5)
    .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('; ');
  const msg = `シナリオ検証エラー [${source}]: ${summary}`;
  if (import.meta.env.DEV || source !== 'live') {
    throw new Error(msg);
  }
  console.warn(msg);
}

export async function bootstrapRuntime(): Promise<BootstrapResult> {
  const params = new URLSearchParams(window.location.search);
  const ticketToken = params.get('ticket');
  const forceMock = params.get('mode') === 'mock';
  const previewId = params.get('previewId');
  const deviceKey = ensureDeviceKey();

  if (ticketToken && !forceMock) {
    const apiBase = import.meta.env.VITE_API_URL || '';
    const api = new PlayerApiClient(apiBase);
    const data = await api.openRun(ticketToken, deviceKey);
    const scenario = data.scenario.content;
    validateScenarioContent(scenario, 'live');
    const engine = new StepEngine(scenario, data.state);
    const runtime = new PlayerRuntime({
      engine,
      persistence: new ApiPersistenceAdapter(api, data.runId, deviceKey),
      mode: 'live',
      context: {
        runId: data.runId,
        deviceKey,
        ticketStatus: data.ticketStatus,
        scenarioTitle: data.scenario.title
      }
    });
    return {
      runtime,
      scenarioId: data.runId, // Live modeではrunIdをscenarioIdとして使用
      scenario
    };
  }

  const scenarioFromPreviewId = previewId ? await fetchPreviewScenario(previewId) : null;
  if (previewId && !scenarioFromPreviewId) {
    throw new Error(`preview scenario が取得できませんでした（期限切れ/Worker未起動の可能性）: ${previewId}`);
  }
  const scenarioParam = params.get('scenario');
  const mockSelection = resolveScenarioByParam(scenarioParam);
  const scenarioForMock = (scenarioFromPreviewId as ScenarioContent) ?? mockSelection.scenario;
  validateScenarioContent(scenarioForMock, scenarioFromPreviewId ? 'preview' : 'mock');
  const engine = new StepEngine(scenarioForMock);
  // P8-0: 完了書に固定文字列（Mock Scenario）を出さないよう、実シナリオタイトルを persistence にも渡す。
  const scenarioTitle =
    typeof scenarioForMock?.meta?.title === 'string'
      ? scenarioForMock.meta.title
      : 'Demo Scenario';
  const runtime = new PlayerRuntime({
    engine,
    persistence: new MockPersistenceAdapter(scenarioTitle),
    mode: 'mock',
    context: {
      runId: 'mock-run',
      deviceKey,
      scenarioTitle
    }
  });
  // P9-2 Part C: 進行保存キーのシナリオ横断衝突の修正。
  // 旧実装は ticketless の全シナリオが共通 id 'mock-scenario' を返し、進行保存キー
  // player_progress_mock-scenario を奪い合っていた（別シナリオを開くとセーブ上書き）。
  // ?scenario= 由来の正規化 id（エイリアスは resolveScenarioByParam が単一 id に潰す）を使い、
  // 旧共有キーに残るセーブは「このシナリオのもの」と確認できた場合のみ一度だけ新キーへ移行する。
  const mockScenarioId = previewId ?? mockSelection.id;
  if (!previewId) {
    migrateLegacyProgress(mockScenarioId, scenarioForMock);
  }
  return {
    runtime,
    scenarioId: mockScenarioId,
    scenario: scenarioForMock
  };
}

async function fetchPreviewScenario(previewId: string): Promise<unknown | null> {
  try {
    const response = await fetch(`/api/v1/preview-scenarios/${encodeURIComponent(previewId)}`, { method: 'GET' });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as any;
    const content = payload?.data?.content ?? null;
    if (!content || typeof content !== 'object' || !Array.isArray(content.nodes)) {
      return null;
    }
    return content;
  } catch {
    return null;
  }
}

